import { useState, useEffect, useCallback } from 'react';
import { GrClose } from 'react-icons/gr';
import Button from './inputs/Button';

import { CANCEL_ACTION } from '../reducers/actionReducer';

export default function ActionModal({ actionState, dispatchAction, show }) {
  const {
    name,
    loading,
    onContinue,
    continueLabel,
    continueLabelOverride,
    continueButtonModifier,
    component,
    onCancel,
    noContinue,
    onSubmit,
    isReactHookForm,
    layout,
    showCancel,
  } = actionState;

  // ATAD-146: the Upload File modal's two-pane shell — a fixed height so
  // switching sources doesn't resize it, and an unpadded body whose panes
  // scroll on their own.
  const panes = layout === 'panes';

  const [isOpen, setIsOpen] = useState(false);

  const handleCancel = useCallback(() => {
    if (!loading) {
      setIsOpen(false);
      if (typeof onCancel === 'function') {
        onCancel();
      }
      setTimeout(() => {
        dispatchAction({
          type: CANCEL_ACTION,
        });
      }, 300);
    }
  }, [loading]);

  function handleContinue() {
    if (loading) {
      return;
    }
    if (typeof onContinue === 'function') {
      onContinue();
    }
  }

  useEffect(() => {
    if (show) {
      setIsOpen(true);
    }
  }, [show]);

  return (
    <div
      className={`fixed inset-0 z-40 flex justify-center items-center transition-opacity duration-300 ease-in-out ${
        isOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
      }`}
      aria-modal="true"
      role="dialog"
    >
      {/* Background Overlay */}
      <div
        className="absolute inset-0 bg-black bg-opacity-50"
        onClick={!loading ? handleCancel : undefined}
      />

      {/* Modal Panel */}
      <div
        className={`relative z-50 flex flex-col w-11/12 max-w-4xl max-h-[95vh] bg-white rounded-lg overflow-hidden ${
          panes
            ? 'h-[760px] shadow-[0_20px_40px_rgba(2,38,60,0.18)]'
            : 'md:w-3/4 lg:w-2/3 shadow-xl'
        }`}
      >
        
        {/* Modal Header */}
        <div
          className={`flex justify-between items-center border-b border-gray-200 flex-shrink-0 ${
            panes ? 'px-6 py-5' : 'p-6'
          }`}
        >
          <h4 className="text-xl font-semibold text-gray-800">{name}</h4>
          <button
            className={`text-gray-400 rounded-full hover:bg-gray-100 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors ${
              panes ? 'w-11 h-11 flex items-center justify-center' : 'p-2'
            }`}
            onClick={handleCancel}
            disabled={loading}
            aria-label="Close modal"
          >
            <GrClose size={20} />
          </button>
        </div>

        {/* Modal Content - Only this area scrolls */}
        <div
          className={
            panes ? 'flex-grow min-h-0 overflow-hidden' : 'flex-grow overflow-y-auto p-6'
          }
        >
          {isReactHookForm ? component : (
            <form
              className={panes ? 'w-full h-full' : 'w-full'}
              onSubmit={(e) => {
                e.preventDefault();
                handleContinue();
              }}
            >
              {component}
            </form>
          )}
        </div>

        {/* Modal Footer */}
        {!noContinue && (
          <div
            className={`flex justify-end items-center gap-3 bg-gray-50 border-t border-gray-200 flex-shrink-0 ${
              panes ? 'px-6 py-5' : 'p-6'
            }`}
          >
            {showCancel && (
              <Button type="button" secondary onClick={handleCancel} disabled={loading}>
                Cancel
              </Button>
            )}
            <Button
              type="button"
              primary={continueButtonModifier === 'primary'}
              secondary={continueButtonModifier === 'secondary'}
              danger={continueButtonModifier === 'danger'}
              onClick={isReactHookForm ? onSubmit : onContinue}
              disabled={loading || (!onContinue && !isReactHookForm)}
            >
              {loading ? 'Loading...' : continueLabelOverride || continueLabel}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
